<template>
  <div class="d-flex justify-content-center align-items-center flex-column">
    <div class="d-flex p-4" style="position: relative;">
      <div id="blob">
        <img src="@/assets/icons/blob.svg" height="200" alt="blob">
      </div>
      <div class="d-flex justify-content-center align-items-center customer flex-column">
        <img src="@/assets/icons/paziente.svg" height="90" alt="customer">
        <p class="p-1">{{ $props.firstName }} {{ $props.lastName }}</p>
      </div>
    </div>
    <div class="d-flex mb-2">
      <input
        type="text"
        id="prescription"
        autocomplete="off"
        class="form-control rounded-custom"
        placeholder="Cerca"
        v-model="searchTerm"
      />
    </div>
  </div>

  <vue-table-lite
    :is-static-mode="true"
    :columns="table.columns"
    @is-finished="tableLoadingFinish"
    :is-loading="table.isLoading"
    :rows="table.rows"
    :total="table.totalRecordCount"
    :sortable="table.sortable"
    :messages="table.messages"
  >
  </vue-table-lite>

  <Modal v-if="showModal"
         :data="scene"
         @hidden="closeModal">
  </Modal>

</template>

<script>
import { defineComponent, reactive, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import VueTableLite from "vue3-table-lite";
import { fetchTemporaryUrl } from "@/use/repositories/assets/fetchTemporaryUrl";
import { error } from "@/use/toast/error";
import { formatDate } from "@/use/utilities/time/formatDate";
import { dateFormatter } from "@/use/utilities/time/dateFormatter";
import Modal from '@/components/Scene/Modal';

export default defineComponent({
  name: "LastAssets",
  props: {
    data: {
      type: Array,
      required: true,
    },
    firstName: {
      type: String,
      required: true,
    },
    lastName: {
      type: String,
      required: true,
    },
  },
  components: {
    VueTableLite,
    Modal,
  },
  setup(props) {
    const i18n = useI18n();
    const searchTerm = ref("");
    const showModal = ref(false);
    const scene = reactive({
      id: null,
      modal_id: null,
      title: null,
      subtitle: null,
      decline_label: null,
      temporary_url: null,
    });

    const formatSeconds = (value) => {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "00";
      }

      return String(date.getSeconds()).padStart(2, "0");
    };

    const formatDateTime = (value) => value
      ? `${formatDate(value)} ${dateFormatter(value, { onlyTime: true })}:${formatSeconds(value)}`
      : "-";

    const formatSingleElement = (row) => {
      const application = row?.application?.toLowerCase?.();
      const zoneNumber = row?.zone_number;

      if (!application || zoneNumber === null || zoneNumber === undefined) {
        return null;
      }

      if (application === "emiarcata") {
        return ({
          1: "D11/18",
          2: "D21/28",
          3: "D31/38",
          4: "D41/48",
        })[zoneNumber] ?? `Emiarcata ${zoneNumber}`;
      }

      if (application === "sestante") {
        return ({
          1: "D14/18",
          2: "D11/13 - D21/23",
          3: "D24/28",
          4: "D24/38",
          5: "D31/33 - D41/43",
          6: "D44/48",
        })[zoneNumber] ?? `Sestante ${zoneNumber}`;
      }

      if (application === "dente") {
        return `D${zoneNumber}`;
      }

      return `${row.application} ${zoneNumber}`;
    };

    const formatElementLabel = (asset) => {
      const rows = [...(asset?.prescription_rows ?? [])].filter(Boolean);

      if (!rows.length) {
        return asset?.type?.name ?? "Generico";
      }

      const bridgeRows = rows.filter((row) => row?.bridge !== null && row?.bridge !== undefined);
      if (bridgeRows.length > 1 && bridgeRows.every((row) => row.bridge === bridgeRows[0].bridge)) {
        const sortedRows = [...bridgeRows].sort((left, right) => Number(left.zone_number) - Number(right.zone_number));
        const firstRow = sortedRows[0];
        const lastRow = sortedRows[sortedRows.length - 1];

        return `PONTE D${firstRow.zone_number}/D${lastRow.zone_number}`;
      }

      const labels = rows
        .map((row) => formatSingleElement(row))
        .filter(Boolean);

      return labels.length ? labels.join(", ") : (asset?.type?.name ?? "Generico");
    };

    const formatStateLabel = (stateSlug) => {
      if (!stateSlug) {
        return "-";
      }

      switch (stateSlug) {
        case "completed":
          return i18n.t("Completed");
        case "pending":
          return i18n.t("Pending");
        case "in-the-works":
          return i18n.t("In the works");
        case "ready-for-oven":
          return i18n.t("Ready for oven");
        case "failed":
          return i18n.t("Failed");
        default:
          return stateSlug
            .split("-")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
      }
    };

    const rows = computed(() => {
      return [...props.data]
        .sort((left, right) => new Date(right.created_at) - new Date(left.created_at))
        .map((asset) => {
          const deletedAt = asset?.deleted_at || null;
          const removedLabel = deletedAt ? i18n.t("Removed on {date}", { date: formatDateTime(deletedAt) }) : "";
          const stateLabel = formatStateLabel(asset?.state?.slug);
          const statusText = [stateLabel, removedLabel].filter(Boolean).join(" - ");

          return {
            id: asset.id,
            created_at_sort: new Date(asset.created_at).getTime(),
            date: formatDateTime(asset.created_at),
            prescription: asset?.prescription?.number_text ?? "-",
            element: formatElementLabel(asset),
            type: asset?.type?.name ?? "-",
            material: asset?.material?.name ?? asset?.prescription?.material ?? "-",
            status_text: statusText || "-",
            status_html: deletedAt
              ? `<span class="history-status-badge">${i18n.t("Removed")}</span><div class="history-status-note">${stateLabel}</div>`
              : `<span>${stateLabel}</span>`,
          };
        });
    });

    const searchValue = computed(() => searchTerm.value.toLowerCase());

    const table = reactive({
      isLoading: false,
      columns: [
        {
          label: "Data",
          field: "date",
          width: "14%",
          sortable: true,
          isKey: true,
        },
        {
          label: "Prescrizione",
          field: "prescription",
          width: "12%",
        },
        {
          label: "Elemento",
          field: "element",
          width: "18%",
        },
        {
          label: "Tipo",
          field: "type",
          width: "14%",
        },
        {
          label: "Materiale",
          field: "material",
          width: "14%",
        },
        {
          label: "Stato",
          field: "status_text",
          width: "16%",
          display: function (row) {
            return row.status_html;
          },
        },
        {
          label: "",
          field: "",
          width: "12%",
          sortable: false,
          display: function (row) {
            return (
              `<a href="javascript:void(0)" class="is-rows-el preview me-4" style="text-decoration-line: none;" data-id="` + row.id + `">
                <svg xmlns="http://www.w3.org/2000/svg" height="35" fill="#A58ABA" class="bi bi-badge-3d-fill" viewBox="0 0 16 16">
                    <path d="M10.157 5.968h-.844v4.06h.844c1.116 0 1.621-.667 1.621-2.02 0-1.354-.51-2.04-1.621-2.04z"/>
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm5.184 4.368c.646 0 1.055.378 1.06.9.008.537-.427.919-1.086.919-.598-.004-1.037-.325-1.068-.756H3c.03.914.791 1.688 2.153 1.688 1.24 0 2.285-.66 2.272-1.798-.013-.953-.747-1.38-1.292-1.432v-.062c.44-.07 1.125-.527 1.108-1.375-.013-.906-.8-1.57-2.053-1.565-1.31.005-2.043.734-2.074 1.67h1.103c.022-.391.383-.751.936-.751.532 0 .928.33.928.813.004.479-.383.835-.928.835h-.632v.914h.663zM8.126 11h2.189C12.125 11 13 9.893 13 7.985c0-1.894-.861-2.984-2.685-2.984H8.126V11z"/>
                </svg>
              </a>
              <a href="javascript:void(0)" class="is-rows-el download" style="text-decoration-line: none;" data-id="` + row.id + `">
                <svg xmlns="http://www.w3.org/2000/svg" height="35" fill="#A58ABA" class="bi bi-cloud-download" viewBox="0 0 16 16">
                  <path d="M4.406 1.342A5.53 5.53 0 0 1 8 0c2.69 0 4.923 2 5.166 4.579C14.758 4.804 16 6.137 16 7.773 16 9.569 14.502 11 12.687 11H10a.5.5 0 0 1 0-1h2.688C13.979 10 15 8.988 15 7.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 2.825 10.328 1 8 1a4.53 4.53 0 0 0-2.941 1.1c-.757.652-1.153 1.438-1.153 2.055v.448l-.445.049C2.064 4.805 1 5.952 1 7.318 1 8.785 2.23 10 3.781 10H6a.5.5 0 0 1 0 1H3.781C1.708 11 0 9.366 0 7.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383z"/>
                  <path d="M7.646 15.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 14.293V5.5a.5.5 0 0 0-1 0v8.793l-2.146-2.147a.5.5 0 0 0-.708.708l3 3z"/>
                </svg>
              </a>`
            );
          },
        },
      ],
      rows: computed(() => {
        return rows.value.filter((row) => {
          const values = [
            row.date,
            row.prescription,
            row.element,
            row.type,
            row.material,
            row.status_text,
            String(row.id),
          ];

          return values.some((value) => value.toLowerCase().includes(searchValue.value));
        });
      }),
      totalRecordCount: computed(() => table.rows.length),
      sortable: {
        order: "created_at_sort",
        sort: "desc",
      },
      messages: {
        pagingInfo: "Mostra {0}-{1} di {2}",
        pageSizeChangeLabel: "Numero righe:",
        gotoPageLabel: "Vai alla pagina:",
        noDataAvailable: "Nessun record trovato",
      },
    });

    const tableLoadingFinish = (elements) => {
      table.isLoading = false;
      Array.prototype.forEach.call(elements, function (element) {
        if (element.classList.contains("preview")) {
          element.addEventListener("click", function () {
            show3D(this.dataset.id);
          });
        }
        if (element.classList.contains("download")) {
          element.addEventListener("click", function () {
            download3D(this.dataset.id);
          });
        }
      });
    };

    const show3D = async (assetId) => {
      try {
        scene.id = assetId;
        scene.modal_id = assetId;
        scene.decline_label = "chiudi";

        const response = await fetchTemporaryUrl(assetId, true);
        scene.temporary_url = response.temporary_url;
        showModal.value = true;
      } catch (errors) {
        error(errors?.[0] ?? "File non disponibile");
      }
    };

    const closeModal = async (value) => {
      scene.show = null;
      scene.id = null;
      scene.decline_label = null;
      scene.temporary_url = null;

      showModal.value = value;
    };

    const download3D = async (assetId) => {
      try {
        const response = await fetchTemporaryUrl(assetId, true);
        window.open(response.temporary_url, "_blank");
      } catch (errors) {
        error(errors?.[0] ?? "File non disponibile");
      }
    };

    return {
      searchTerm,
      table,
      tableLoadingFinish,
      showModal,
      scene,
      closeModal,
    };
  },
});

</script>

<style scoped>
#blob {
  position: absolute;
  z-index: 1;
  transform: rotate(368deg);
  top: -12%;
  left: 2%;
}
.customer {
  z-index: 2;
}
.card {
  border-top: none;
}

/* Change style table */
::v-deep(.vtl-table .vtl-thead .vtl-thead-th) {
  color: #000000;
  background-color: #F3E9FF;
  border-color: #F3E9FF;
}
::v-deep(.vtl-table td),
::v-deep(.vtl-table tr) {
  border: none;
  border-bottom: 1px solid #9D9D9D;
  vertical-align: middle;
}
::v-deep(.vtl-paging-info),
::v-deep(.vtl-paging-pagination-div),
::v-deep(.vtl-paging-change-div) {
  padding-top: 30px;
  color: #7E6492;
}
::v-deep(.vtl-paging-count-label),
::v-deep(.vtl-paging-page-label) {
  color: #7E6492;
}
::v-deep(.vtl-paging-pagination-page-link) {
  border: none;
}
::v-deep(.history-status-badge) {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: #F3E9FF;
  color: #7E6492;
  font-size: 12px;
  font-weight: 600;
}
::v-deep(.history-status-note) {
  margin-top: 4px;
  color: #6c757d;
  font-size: 12px;
}
</style>
